import React from "react";
import lazyPage from "../utils/lazyPage";
import { Routes, Route, Navigate } from "react-router-dom";
const CustomerLogin = lazyPage(() => import("../pages/CustomerLogin"));
const CustomerDashboard = lazyPage(() => import("../pages/CustomerDashboard"));
const CustomerResetPassword = lazyPage(() => import("../pages/CustomerResetPassword"));
const DriverPortal = lazyPage(() => import("../pages/DriverPortal"));
const StaffClockPage = lazyPage(() => import("../pages/StaffClockPage"));
const KnowledgeBase = lazyPage(() => import("../pages/KnowledgeBase"));
const ShopifySSORedirect = lazyPage(() => import("../pages/ShopifySSORedirect"));
const TenantLoginPage = lazyPage(() => import("../pages/TenantLoginPage"));
const ResetPasswordPage = lazyPage(() => import("../pages/ResetPasswordPage"));
// CustomerStorefront retired v4.3.1 (tenant hosts) and DELETED v4.9.1 —
// every storefront route on every host uses MealPassProgramStore.
const StorefrontCheckout = lazyPage(() => import("../pages/StorefrontCheckout"));
const MealPackPayment = lazyPage(() => import("../pages/MealPackPayment"));
const OrderSuccess = lazyPage(() => import("../pages/OrderSuccess"));
const PaymentSuccess = lazyPage(() => import("../pages/PaymentSuccess"));
const ThemeEditor = lazyPage(() => import("../pages/ThemeEditor"));
const TenantHome = lazyPage(() => import("../pages/TenantHome"));
import TenantRootRouter from "../components/TenantRootRouter";
import DeliveryTrackingPage from "../components/public/DeliveryTrackingPage";
import ShortTrackingRedirect from "../components/public/ShortTrackingRedirect";
const PitchDeck = lazyPage(() => import("../pages/PitchDeck"));
const LandingPage = lazyPage(() => import("../pages/LandingPage"));
import MealPassProgramStore from "../components/customer/mealpass/MealPassProgramStore";
// v6 - the rebuilt storefront (design system); the v5 page stays mounted at /meal-passes until every pin migrates
import Storefront from "../components/customer/storefront/Storefront";
import MenuDirectory from "../components/customer/storefront/MenuDirectory";
const QROrderPage = lazyPage(() => import("../pages/QROrderPage"));  // v5.7 — QR scan-to-order
const MarketplacePage = lazyPage(() => import("../pages/MarketplacePage"));  // Marketplace v1 — address-first front door
const CateringCityPage = lazyPage(() => import("../pages/CateringCityPage"));        // v5.29 — the unified catering list (mirror path)
const CateringListingPage = lazyPage(() => import("../pages/CateringListingPage"));
const YofoLegalPage = lazyPage(() => import("../pages/YofoLegalPage"));  // YOFO legal policies (/legal/:doc)
const ClaimStorePage = lazyPage(() => import("../pages/ClaimStorePage"));
const PublicQuotePage = lazyPage(() => import("../pages/PublicQuotePage"));
const PublicAgreementPage = lazyPage(() => import("../pages/PublicAgreementPage"));
import MealPlansCatalogue from "../components/customer/mealpass/MealPlansCatalogue";
import StorefrontAboutPage from "../components/customer/mealpass/StorefrontAboutPage";
import StorefrontContactPage from "../components/customer/mealpass/StorefrontContactPage";
import { ProtectedRoute } from "../components/auth";
import {
  RedirectWithParams,
  TenantAdminRoute,
  TenantBillingRoute,
} from "../components/auth/RouteGuards";

const TenantRoutes = ({ tenantSubdomain }) => (
  <Routes>
    {/* Tenant Root */}
    <Route path="/" element={<TenantRootRouter tenantSubdomain={tenantSubdomain} />} />
    <Route path="/pitch-deck" element={<PitchDeck />} />
    <Route path="/preview-landing" element={<LandingPage />} />
    <Route path="/login" element={<TenantLoginPage tenantSubdomain={tenantSubdomain} />} />
    <Route path="/reset-password" element={<ResetPasswordPage tenantSubdomain={tenantSubdomain} />} />

    {/* Public claim/review/publish page (token = capability, no auth) */}
    <Route path="/claim/:token" element={<ClaimStorePage />} />

    {/* v4.31 — public catering quote pay/review page (token = capability, no auth) */}
    <Route path="/catering/quote/:token" element={<PublicQuotePage />} />

    {/* v4.31.1 — public agreement signing page (token = capability, no auth) */}
    <Route path="/agreement/:token" element={<PublicAgreementPage />} />

    {/* Customer Portal — specific routes before parameterized */}
    <Route path="/customers/theme/edit" element={<ThemeEditor />} />
    <Route path="/customers/:storeSlug/theme/edit" element={<ThemeEditor />} />
    <Route path="/customers/login" element={<CustomerLogin />} />
    <Route path="/customers/checkout" element={<StorefrontCheckout />} />
    <Route path="/meal-pack-payment/:token" element={<MealPackPayment />} />
    {/* v4.3.1 — MealPassProgramStore is now THE unified storefront for every route */}
    <Route path="/customers/meal-passes" element={<MealPassProgramStore />} />
    <Route path="/customers/:storeSlug/meal-passes" element={<MealPassProgramStore />} />
    {/* v4.3.4 — Full meal-plans catalogue with search + tag filter + sort */}
    <Route path="/customers/plans" element={<MealPlansCatalogue />} />
    <Route path="/customers/:storeSlug/plans" element={<MealPlansCatalogue />} />
    <Route path="/customers/checkout/success" element={<OrderSuccess />} />
    <Route path="/customers/dashboard" element={<CustomerDashboard />} />
    <Route path="/customers/:storeSlug/dashboard" element={<CustomerDashboard />} />
    <Route path="/customers/reset-password" element={<CustomerResetPassword />} />
    <Route path="/customers/:storeSlug/reset-password" element={<CustomerResetPassword />} />
    <Route path="/customers/:storeSlug/login" element={<CustomerLogin />} />
    <Route path="/customers/:storeSlug/checkout" element={<StorefrontCheckout />} />
    <Route path="/customers/:storeSlug/checkout/success" element={<OrderSuccess />} />
    <Route path="/customers/:storeSlug/dashboard" element={<CustomerDashboard />} />
    {/* Retired v4.3.1 — legacy CustomerStorefront replaced by MealPassProgramStore.
        Every storefront URL now routes to the unified cinematic landing page.
        Existing checkout/dashboard routes above remain untouched. */}
    {/* v4.25 — dedicated SEO pages (must precede the :storeSlug catch-all) */}
    <Route path="/customers/menu" element={<MenuDirectory />} />
    <Route path="/customers/menu/:itemId" element={<MenuDirectory />} />
    <Route path="/customers/:storeSlug/menu" element={<MenuDirectory />} />
    <Route path="/customers/:storeSlug/menu/:itemId" element={<MenuDirectory />} />
    <Route path="/customers/about" element={<StorefrontAboutPage />} />
    <Route path="/customers/:storeSlug/about" element={<StorefrontAboutPage />} />
    <Route path="/customers/contact" element={<StorefrontContactPage />} />
    <Route path="/customers/:storeSlug/contact" element={<StorefrontContactPage />} />
    {/* v5.7 — QR scan-to-order landing (no login); reads ?tenant=&store_slug=&table= */}
    <Route path="/order" element={<QROrderPage />} />
    {/* Marketplace: /marketplace is the LANDING; /marketplace/browse is the browse
        surface (founder). One splat route keeps the page mounted across the two. */}
    {/* v5.29 — the landing's catering CTA lands on /marketplace/catering/:city on a tenant
        subdomain too (Codex P2: the splat below never mounted the directory pages) */}
    <Route path="/marketplace/catering/:city" element={<CateringCityPage />} />
    <Route path="/marketplace/catering/:city/occasion/:landingValue" element={<CateringCityPage landing="occasion" />} />
    <Route path="/marketplace/catering/:city/cuisine/:landingValue" element={<CateringCityPage landing="cuisine" />} />
    <Route path="/marketplace/catering/:city/:slug" element={<CateringListingPage />} />
    <Route path="/marketplace/*" element={<MarketplacePage />} />
    <Route path="/legal/:doc" element={<YofoLegalPage />} />
    <Route path="/customers/:storeSlug" element={<Storefront />} />
    <Route path="/customers" element={<Storefront />} />

    {/* Shopify SSO redirect */}
    <Route path="/shopify-sso" element={<ShopifySSORedirect />} />

    {/* /customer → /customers redirects */}
    <Route path="/customer/theme/edit" element={<Navigate to="/customers/theme/edit" replace />} />
    <Route path="/customer/login" element={<Navigate to="/customers/login" replace />} />
    <Route path="/customer/checkout/success" element={<Navigate to="/customers/checkout/success" replace />} />
    <Route path="/customer/checkout" element={<Navigate to="/customers/checkout" replace />} />
    <Route path="/customer/dashboard" element={<Navigate to="/customers/dashboard" replace />} />
    <Route path="/customer/reset-password" element={<Navigate to="/customers/reset-password" replace />} />
    <Route path="/customer/:storeSlug/theme/edit" element={<RedirectWithParams to="/customers/:storeSlug/theme/edit" />} />
    <Route path="/customer/:storeSlug/dashboard" element={<RedirectWithParams to="/customers/:storeSlug/dashboard" />} />
    <Route path="/customer/:storeSlug/reset-password" element={<RedirectWithParams to="/customers/:storeSlug/reset-password" />} />
    <Route path="/customer/:storeSlug/login" element={<RedirectWithParams to="/customers/:storeSlug/login" />} />
    <Route path="/customer/:storeSlug/checkout/success" element={<RedirectWithParams to="/customers/:storeSlug/checkout/success" />} />
    <Route path="/customer/:storeSlug/checkout" element={<RedirectWithParams to="/customers/:storeSlug/checkout" />} />
    <Route path="/customer/:storeSlug" element={<RedirectWithParams to="/customers/:storeSlug" />} />
    <Route path="/customer" element={<Navigate to="/customers" replace />} />

    {/* Payment */}
    <Route path="/payment-success" element={<PaymentSuccess />} />
    <Route path="/payment-cancelled" element={<PaymentSuccess />} />

    {/* Admin Billing (must be before /admin/* catch-all) */}
    <Route path="/admin/billing/success" element={<TenantBillingRoute tenantSubdomain={tenantSubdomain} />} />
    <Route path="/admin/billing" element={<TenantBillingRoute tenantSubdomain={tenantSubdomain} />} />

    {/* Admin */}
    <Route path="/admin/*" element={<TenantAdminRoute tenantSubdomain={tenantSubdomain} />} />

    {/* Knowledge Base (public) */}
    <Route path="/knowledge-base" element={<KnowledgeBase />} />
    <Route path="/knowledge-base/:slug" element={<KnowledgeBase />} />

    {/* Driver & Tracking */}
    <Route path="/driver/:magicToken" element={<DriverPortal />} />
    <Route path="/team" element={<StaffClockPage />} />
    <Route path="/staff-clock" element={<StaffClockPage />} />
    <Route path="/track/:trackingToken" element={<DeliveryTrackingPage />} />
    <Route path="/t/:shortCode" element={<ShortTrackingRedirect />} />

    {/* Tenant Home (after login) */}
    <Route path="/app" element={<ProtectedRoute><TenantHome /></ProtectedRoute>} />
    <Route path="/dashboard/*" element={<TenantAdminRoute tenantSubdomain={tenantSubdomain} />} />

    {/* Billing */}
    <Route path="/billing" element={<TenantBillingRoute tenantSubdomain={tenantSubdomain} />} />
    <Route path="/billing/success" element={<TenantBillingRoute tenantSubdomain={tenantSubdomain} />} />
  </Routes>
);

export default TenantRoutes;
